import {
  SkuSlaPricingModelEnum,
  SkuSlaSupplierConfigDTO,
  SkuSlaSupplierQtyRangeTypeEnum,
  SkuSlaSupplierRangeDTO,
} from '../../../../../domains/slaDomain';
import { Collapse, Form, Radio } from 'antd';
import { UnderlineText } from '../../../../../components/RevxTypography';
import RevxFlexLayout from '../../../../../components/RevxFlexLayout';
import { RevxInputNumber } from '../../../../../components/RevxInputNumber';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceFn } from 'ahooks';
import {
  AutoSplitQuantity,
  rangeTypeEnum,
  autoSplitQuantity,
} from './slaSplitQuantity';
import { DEBOUNCE_TIME, NOT_APPLICABLE } from '../../../../../utils/constants';
import { RevxMoney } from '../../../../../components/RevxMoney';
import { FileDeleteIcon } from '../../../../../components/Icons';
import slaService from '../../../../../services/slaService';
import { useAppDispatch } from '../../../../../app/hooks';
import { toast } from '../../../../../components/RevxMessage';
import { FormListOperation } from 'antd/es/form/FormList';

const { updateSlaSupplier } = slaService;
const { STANDARD_ALONE, CONTINUOUS } = SkuSlaSupplierQtyRangeTypeEnum;

type SLASupplierPriceProps = {
  id: number;
  config: SkuSlaSupplierConfigDTO;
};

const MAX_QUANTITY = 10000;
const openKeys = ['1'];

const SLASupplierPrice = ({ id, config }: SLASupplierPriceProps) => {
  const { sla } = useAppDispatch();
  const { t } = useTranslation();
  const [supplierUnitPriceForm] = Form.useForm<SkuSlaSupplierConfigDTO>();

  useEffect(() => {
    supplierUnitPriceForm.setFieldsValue(config);
  }, [config, supplierUnitPriceForm]);

  const initSupplierQuantityRange = (): SkuSlaSupplierRangeDTO => {
    return {
      high: supplierUnitPriceForm.getFieldsValue().maxQty || MAX_QUANTITY,
      low: 1,
      priceToPaySupplier: 0,
      pricingEnginePrice: 0,
    };
  };

  const { run: runAutoSplitQuantity, cancel: cancelAutoSplitQuantity } =
    useDebounceFn(
      ({ index, level, value }: AutoSplitQuantity) => {
        autoSplitQuantity({ index, level, value }, supplierUnitPriceForm, true);
      },
      { wait: DEBOUNCE_TIME }
    );

  const { run: runUpdateSlaSupplier } = useDebounceFn(
    async () => {
      await updateSlaSupplierHandler();
    },
    {
      wait: DEBOUNCE_TIME + 200,
    }
  );

  const updateSlaSupplierHandler = async () => {
    const newAllData = supplierUnitPriceForm.getFieldsValue();
    const res = await updateSlaSupplier(id, {
      ...config,
      ...newAllData,
    });
    sla.updateSlaSupplier(res);
  };

  const renderCollapseHeader = () => {
    return (
      <RevxFlexLayout className="panel-header" justify="space-between">
        <RevxFlexLayout className="panel-header__main">
          <div className="quantity-model mr-50">
            <Form.Item name="qtyRangeType">
              <Radio.Group onChange={(v) => {}}>
                <Radio value={STANDARD_ALONE}>
                  {t('platform.sku.unitPrice.quantityModelRadio.alone')}
                </Radio>
                <Radio value={CONTINUOUS}>
                  {t('platform.sku.unitPrice.quantityModelRadio.continue')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </div>
        </RevxFlexLayout>
      </RevxFlexLayout>
    );
  };

  const renderCollapseContent = () => {
    return (
      <RevxFlexLayout className="panel-content">
        <Form.Item noStyle dependencies={['qtyRangeType']}>
          {({ getFieldsValue }) => {
            const { qtyRangeType } = getFieldsValue();
            const showActionBtn = qtyRangeType === STANDARD_ALONE;
            let addHandler: FormListOperation['add'];
            return (
              <>
                <Form.List name={'ranges'}>
                  {(fields, { add, remove }) => {
                    addHandler = add;
                    return (
                      <>
                        {fields.map((field, index) => {
                          const genNameField = (name: string) => [
                            field.key,
                            name,
                          ];
                          return (
                            <RevxFlexLayout
                              className="panel-content__main"
                              key={index}
                              justify="space-between"
                            >
                              <RevxFlexLayout className="mb-10">
                                <RevxFlexLayout className="quantity-block mr-100">
                                  <Form.Item
                                    name={genNameField('low')}
                                    label="Low Quantity"
                                    className="mr-24"
                                  >
                                    <RevxInputNumber
                                      precision={0}
                                      min={0}
                                      onChange={(value) => {
                                        if (showActionBtn) return;
                                        cancelAutoSplitQuantity();
                                        runAutoSplitQuantity({
                                          index,
                                          level: rangeTypeEnum.low,
                                          value,
                                        });
                                      }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    name={genNameField('high')}
                                    label="High Quantity"
                                  >
                                    <RevxInputNumber
                                      min={0}
                                      precision={0}
                                      onChange={(value) => {
                                        if (showActionBtn) return;
                                        cancelAutoSplitQuantity();
                                        runAutoSplitQuantity({
                                          index,
                                          level: rangeTypeEnum.high,
                                          value,
                                        });
                                      }}
                                    />
                                  </Form.Item>
                                </RevxFlexLayout>
                                <RevxFlexLayout className="pricing-block mr-100">
                                  <div className="label">
                                    Pricing engine price
                                  </div>
                                  <Form.Item
                                    name={genNameField('pricingEnginePrice')}
                                  >
                                    <PricingEnginePrice />
                                  </Form.Item>
                                </RevxFlexLayout>
                                <RevxFlexLayout className="pricing-block mr-100">
                                  <Form.Item
                                    name={genNameField('priceToPaySupplier')}
                                    label="Price to pay supplier"
                                  >
                                    <RevxInputNumber
                                      prefix="$"
                                      precision={6}
                                      min={0}
                                    />
                                  </Form.Item>
                                </RevxFlexLayout>
                              </RevxFlexLayout>
                              {showActionBtn && (
                                <RevxFlexLayout
                                  className="quantity-close click"
                                  onClick={() => {
                                    remove(field.key);
                                  }}
                                >
                                  <FileDeleteIcon />
                                </RevxFlexLayout>
                              )}
                            </RevxFlexLayout>
                          );
                        })}
                      </>
                    );
                  }}
                </Form.List>
                {showActionBtn && (
                  <RevxFlexLayout className="panel-content__footer">
                    <UnderlineText
                      color="green"
                      onClick={() => {
                        addHandler(initSupplierQuantityRange());
                      }}
                    >
                      +Add quantity range
                    </UnderlineText>
                  </RevxFlexLayout>
                )}
              </>
            );
          }}
        </Form.Item>
      </RevxFlexLayout>
    );
  };

  const formValuesChangeHandler = async (
    e: any,
    allValues: SkuSlaSupplierConfigDTO
  ) => {
    try {
      await runUpdateSlaSupplier();
    } catch (e) {
      toast.error(e.originalMessage);
    }
  };
  return (
    <div className="mb-10">
      <Collapse>
        <Collapse.Panel header="Manage supplier price" key="1">
          <div>
            <div className="unit-price">
              <div className="unit-price__title">
                <h2>{t('platform.sku.unitPrice.supplierPrice')}</h2>
                <div className="bold">
                  {t('platform.sku.unitPrice.pricingTips')}
                </div>
              </div>
              <Form
                form={supplierUnitPriceForm}
                requiredMark={false}
                onValuesChange={formValuesChangeHandler}
              >
                {/*<Form.Item
                  name="pricingModel"
                  label={t('platform.sku.unitPrice.priceModelLabel')}
                >
                  <Radio.Group>
                    <Radio value={SkuSlaPricingModelEnum.PRICING_ENGINE}>
                      {t('platform.sku.unitPrice.priceModelRadio.pricing')}
                    </Radio>
                    <Radio value={SkuSlaPricingModelEnum.UNIT_PRICE}>
                      {t('platform.sku.unitPrice.priceModelRadio.unit')}
                    </Radio>
                  </Radio.Group>
                </Form.Item>*/}
                <Form.Item noStyle={true} dependencies={['pricingModel']}>
                  {({ getFieldValue }) => {
                    const isUnitPriceModel =
                      getFieldValue('pricingModel') ===
                      SkuSlaPricingModelEnum.UNIT_PRICE;
                    return (
                      isUnitPriceModel && (
                        <>
                          <RevxFlexLayout className="unit-price__quantity mb-24">
                            <Form.Item
                              name="maxQty"
                              label={t(
                                'platform.sku.unitPrice.maxQuantityLabel'
                              )}
                            >
                              <RevxInputNumber min={0} />
                            </Form.Item>
                          </RevxFlexLayout>
                          <Collapse
                            activeKey={openKeys}
                            collapsible={'disabled'}
                            className="mb-24"
                          >
                            <Collapse.Panel
                              header={renderCollapseHeader()}
                              key={'1'}
                              showArrow={false}
                            >
                              {renderCollapseContent()}
                            </Collapse.Panel>
                          </Collapse>
                        </>
                      )
                    );
                  }}
                </Form.Item>
              </Form>
            </div>
          </div>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export default SLASupplierPrice;

const PricingEnginePrice = (v: { value?: number }) => {
  const value = v?.value;
  return value ? (
    <RevxMoney value={value} decimalDigits={6} />
  ) : (
    <span>{NOT_APPLICABLE}</span>
  );
};
